<template>
    <div>
        <div v-if="blocks && !blocks.length">
            <v-btn block x-large @click="addAt(0)">Add a Content Block!</v-btn>
        </div>
        <draggable
            v-else
            tag="div"
            :list="blocks"
            class="list-group mt-6"
            handle=".handle"
        >
            <v-card
                v-for="(element, idx) in blocks"
                :key="element.key"
                class="list-group-item block mb-12 mt-10 mx-5"
                outlined
            >
                <div :class="collapsedClasses(['fab-container'])">
                    <v-menu offset-y max-height="55vh">
                        <template #activator="{ on }">
                            <v-btn class="handle mx-1" outlined color="gray" small fab v-on="on">
                                <v-icon small>fa fa-plus</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item @click="addAt(idx)">
                                <v-list-item-title>Add Block Above</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="addAt(idx + 1)">
                                <v-list-item-title>Add Block Below</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="copyAt(idx)">
                                <v-list-item-title>Copy Block</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="pasteAt(idx)">
                                <v-list-item-title>Paste Block Above</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="pasteAt(idx + 1)">
                                <v-list-item-title>Paste Block Below</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="removeAt(idx)">
                                <v-list-item-title>Cut/Remove Block</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-menu offset-y max-height="33vh">
                        <template #activator="{ on }">
                            <v-btn class="handle mx-1" outlined color="gray" height="40px" v-on="on">
                                <v-icon small left>fa fa-cubes</v-icon>
                                {{ getBlockTypeLabel(element) }}
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item
                                v-for="type in availableTypes"
                                :key="type.value"
                                @click="changeBlockType(idx, type.value)"
                            >
                                <v-list-item-title>{{ type.label }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-menu offset-y>
                        <template #activator="{ on }">
                            <v-btn class="handle mx-1" outlined color="gray" small fab v-on="on">
                                <v-icon small>fa fa-paint-brush</v-icon>
                            </v-btn>
                        </template>
                        <v-card>
                            <v-card-text>
                                <v-row>
                                    <v-col
                                        v-for="(style, index) in availableStyles"
                                        :key="style + index"
                                        cols="6"
                                        md="3"
                                    >
                                        <v-checkbox v-model="blocks[idx].styles" :label="style" :value="style" />
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-menu>
                    <v-menu offset-y>
                        <template #activator="{ on }">
                            <v-btn class="handle mx-1" outlined color="gray" small fab v-on="on" @click="toggleBlockVisibility(idx)">
                                <v-icon small :class="publishStatusClass(element.status)">
                                    {{ element.status === 'draft' ? 'fa fa-eye-slash' : 'fa fa-eye' }}
                                </v-icon>
                            </v-btn>
                        </template>
                    </v-menu>
                    <v-menu offset-y>
                        <template #activator="{ on }">
                            <v-btn
                                class="handle mx-1"
                                outlined color="gray"
                                :small="!publishingDates(element)"
                                :fab="!publishingDates(element)"
                                :height="publishingDates(element) ? '40px' : ''"
                                v-on="on"
                            >
                                <v-icon small>fa fa-calendar-alt</v-icon>
                                <span v-html="publishingDates(element)" />
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item>
                                <date-selector v-model="element.startDate" label="Start Date" />
                            </v-list-item>
                            <v-list-item>
                                <date-selector v-model="element.endDate" label="End Date" />
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-menu offset-y :close-on-content-click="false">
                        <template #activator="{ on }">
                            <v-btn class="handle mx-1" outlined color="gray" small fab v-on="on">
                                <v-icon small>fa fa-filter</v-icon>
                            </v-btn>
                        </template>
                        <v-card>
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12" class="pb-0">
                                        <v-radio-group v-model="blocks[idx].permissions.exclude" row dense>
                                            <v-radio label="Include" value="include" />
                                            <v-radio label="Exclude" value="exclude" />
                                        </v-radio-group>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-select multiple :items="roleItems" v-model="blocks[idx].permissions.role" label="Roles" />
                                    </v-col>
                                    <v-col cols="6">
                                        <v-select multiple :items="permissionItems" v-model="blocks[idx].permissions.permission" label="Permissions" />
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-menu>
                    <v-btn class="handle" dark color="gray" small fab absolute right>
                        <v-icon small>fas fa-arrows-alt</v-icon>
                    </v-btn>
                </div>
                <div :class="blockClasses(blocks[idx], 'pa-2 mb-6 block-container')">
                    <div :is="element.type + '-editor'" v-model="blocks[idx]" />
                </div>
            </v-card>
        </draggable>
        <div v-if="blocks && blocks.length" class="text-center my-4">
            <v-btn x-large @click="addAt(blocks.length)">
                <v-icon left small>fas fa-plus</v-icon>
                New Content Block
            </v-btn>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import moment from 'moment'

import AccordionEditor from './components/editors/AccordionEditor.vue'
import AnchorEditor from './components/editors/AnchorEditor.vue'
import SpacerEditor from './components/editors/SpacerEditor.vue'
import FullWidthTextEditor from './components/editors/FullWidthTextEditor.vue'
import ZendeskTextEditor from './components/editors/ZendeskTextEditor.vue'
import TwoCol5050Editor from './components/editors/TwoCol5050Editor.vue'
import ButtonGroupEditor from './components/editors/ButtonGroupEditor.vue'
import CardGroupEditor from './components/editors/CardGroupEditor.vue'
import WistiaVideoGroupEditor from './components/editors/WistiaVideoGroupEditor.vue'
import IframeGroupEditor from './components/editors/IframeGroupEditor.vue'
import BannerEditor from './components/editors/BannerEditor.vue'
import LeaderboardEditor from './components/editors/LeaderboardEditor.vue'
import ScriptEmbedGroupEditor from './components/editors/ScriptEmbedGroupEditor.vue'
import TwoCol7525Editor from './components/editors/TwoCol7525Editor.vue'
import TwoCol2575Editor from './components/editors/TwoCol2575Editor.vue'
import CallToActionEditor from './components/editors/CallToActionEditor.vue'
import FourCol25252525Editor from './components/editors/FourCol25252525Editor.vue'
import ThreeCol333333Editor from './components/editors/ThreeCol333333Editor.vue'
import ThreeCol255025Editor from './components/editors/ThreeCol255025Editor.vue'
import CarrierContactInfoEditor from './components/editors/CarrierContactInfoEditor.vue'
import CarrierProductsEditor from './components/editors/CarrierProductsEditor.vue'
import AcknowledgeCheckBoxEditor from './components/editors/AcknowledgeCheckBoxEditor.vue'
import DataTableGroupEditor from './components/editors/DataTableGroupEditor.vue'
import Styles from './components/Styles'
import ImageSelector from './components/ImageSelector.vue'
import DateSelector from './components/DateSelector.vue'
import SummitCalculatorEditor from '@/CMS/editor/components/editors/SummitCalculatorEditor'
import MarketingResourceEditor from '@/CMS/editor/components/editors/MarketingResourceEditor'

export default {
    name: 'QEditPageBlocks',
    components: {
        draggable,
        AccordionEditor,
        AnchorEditor,
        FullWidthTextEditor,
        SpacerEditor,
        TwoCol5050Editor,
        ButtonGroupEditor,
        CardGroupEditor,
        IframeGroupEditor,
        CallToActionEditor,
        FourCol25252525Editor,
        ThreeCol333333Editor,
        ThreeCol255025Editor,
        TwoCol2575Editor,
        TwoCol7525Editor,
        BannerEditor,
        LeaderboardEditor,
        ZendeskTextEditor,
        ImageSelector,
        DateSelector,
        ScriptEmbedGroupEditor,
        CarrierContactInfoEditor,
        CarrierProductsEditor,
        AcknowledgeCheckBoxEditor,
        WistiaVideoGroupEditor,
        DataTableGroupEditor,
        SummitCalculatorEditor,
        MarketingResourceEditor,
    },
    props: {
        value: Array,
    },
    data () {
        return {
            counter: Math.floor(Math.random() * 1000000000), // TODO: should we replace this?
            availableBlockTypes: [{
                label: 'Accordion',
                value: 'accordion'
            }, {
                label: 'Anchor',
                value: 'anchor'
            }, {
                label: 'Banner',
                value: 'banner'
            }, {
                label: 'Leaderboard',
                value: 'leaderboard',
                feature: 'DashboardLeaderboard'
            }, {
                label: 'Data Table',
                value: 'data-table-group'
            }, {
                label: 'Full Width Text Block',
                value: 'full-width-text'
            }, {
                label: 'Two Columns 50/50',
                value: 'two-col-5050'
            }, {
                label: 'Two Columns 25/75',
                value: 'two-col-2575'
            }, {
                label: 'Two Columns 75/25',
                value: 'two-col-7525'
            }, {
                label: 'Three Columns 33/33/33',
                value: 'three-col-333333'
            }, {
                label: 'Three Columns 25/50/25',
                value: 'three-col-255025'
            }, {
                label: 'Four Columns 25/25/25/25',
                value: 'four-col-25252525'
            }, {
                label: 'Card Group',
                value: 'card-group'
            }, {
                label: 'Wistia Video Group',
                value: 'wistia-video-group'
            }, {
                label: 'Iframe Group',
                value: 'iframe-group'
            }, {
                label: 'Script/Embed Group',
                value: 'script-embed-group'
            }, {
                label: 'Button Group',
                value: 'button-group'
            }, {
                label: 'Call to Action',
                value: 'call-to-action'
            }, {
                label: 'Spacer',
                value: 'spacer'
            }, {
                label: 'Carrier Contact Info',
                value: 'carrier-contact-info'
            }, {
                label: 'Carrier Products',
                value: 'carrier-products'
            }, {
                label: 'Acknowledge Checkbox',
                value: 'acknowledge-check-box'
            }, {
                label: 'Summit Calculator',
                value: 'summit-calculator',
                feature: 'Summit'
            }, {
                label: 'Marketing Resource',
                value: 'marketing-resource',
                permission: 'manage:MarketingResourceLibrary'
            }],
            availableStyles: Styles.BlockStyles,
            collapseBlocks: false,
        }
    },
    computed: {
        roleItems () {
            return [ 'Any', ...this.role_choices]
        },
        permissionItems () {
            return ['Any', ...this.permission_choices]
        },
        blocks () {
            return this.value
        },
        availableTypes () {
            return this.availableBlockTypes
                .filter((type) => !type.feature || this.tenantHasFeature(type.feature))
                .filter((type) => type.permission ? this.hasPermission(type.permission) : true)
        }
    },
    methods: {
        removeAt (idx) {
            localStorage.setItem('copiedBlock', JSON.stringify(this.blocks[idx]))
            this.blocks.splice(idx, 1)
        },
        addAt (idx) {
            this.counter += 1
            this.blocks.splice(idx, 0, {
                id: this.counter, // TODO: should we replace this?
                type: 'full-width-text',
                text: 'Sed nec diam non eros lobortis tempor et eget sapien. Sed rutrum orci eget ligula mattis, vel vestibulum ligula feugiat. Cras et velit ut ipsum fringilla vulputate.',
                hasFocus: false,
                styles: [],
                permissions: { role: null, permission: null },
                CarrierID: this.carrier_id,
                CarrierIDs: [this.carrier_id],
                key: Math.random() // TODO: should we replace this?
            })
        },
        copyAt (idx) {
            localStorage.setItem('copiedBlock', JSON.stringify(this.blocks[idx]))
        },
        pasteAt (idx) {
            if (localStorage.getItem('copiedBlock') === null) return
            const block = JSON.parse(localStorage.getItem('copiedBlock'))
            this.counter += 1
            block.id = this.counter // TODO: should we replace this?
            block.key = Math.random() // TODO: should we replace this?
            this.blocks.splice(idx, 0, block)
        },
        changeBlockType (idx, type) {
            this.blocks[idx].type = type
            if (typeof this.blocks[idx].styles === 'undefined') {
                this.blocks[idx].styles = []
            }
        },
        collapsedClasses (classes) {
            if (this.collapseBlocks) {
                classes.push('collapsed')
            }
            return classes.join(' ')
        },
        getBlockTypeLabel (block) {
            return this.titleCase(block.type.replaceAll('-', ' '))
        },
        toggleBlockVisibility (idx) {
            this.blocks[idx].status = this.blocks[idx].status !== 'draft' ? 'draft' : 'publish'
        },
        publishingDates (element) {
            if (!element.startDate && !element.endDate) return ''
            const start = element.startDate ? moment(element.startDate).format('l') : ''
            const end = element.endDate ? moment(element.endDate).format('l') : ''
            return `<span class="ml-2">${start} - ${end}</span>`
        },
        publishStatusClass (status) {
            return status === 'draft' ? 'unpublished-block' : ''
        },
    },
}
</script>

<style scoped lang="scss">
.block {
    position: relative;
}

.fab-container {
    padding: 15px;
    .theme--light & {
      background-color: #f7f7f7;
    }
    .theme--dark & {
      background-color: #414141;
    }
    padding-right: 60px;
}

.unpublished-block {
    color: rgba(0, 0, 0, 0.1) !important;
}

.fab-container.collapsed {
    position: relative;
}

.block-container.collapsed {
    display: none;
}
</style>
